import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaDiscord, FaTwitch } from 'react-icons/fa';
import { SiItchdotio } from 'react-icons/si';
import { useTranslation } from 'react-i18next';

/**
 * Footer component for the website
 * Contains contact information, navigation links, and social media
 * Uses Tailwind CSS for styling with a gaming-friendly design
 * Supports multiple languages through i18n
 */
const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-primary text-white py-20 relative overflow-hidden">
      {/* Gaming-inspired decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-accent"></div>
      <div className="absolute top-10 right-10 w-8 h-8 bg-secondary rotate-45"></div>
      <div className="absolute bottom-20 left-20 w-6 h-6 bg-accent"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="/natux-logo.png" 
                alt="NatuX Translations Logo" 
                className="h-10"
              />
            </div>
            <p className="text-white/80 leading-relaxed mb-8">
              Professional French game localization services for developers worldwide.
              We help make your games accessible to French-speaking players.
            </p>
            <div className="flex space-x-4">
              <SocialLink href="https://facebook.com" icon={<FaFacebookF />} label="Facebook" />
              <SocialLink href="https://twitter.com" icon={<FaTwitter />} label="Twitter" />
              <SocialLink href="https://linkedin.com" icon={<FaLinkedinIn />} label="LinkedIn" />
              <SocialLink href="https://discord.com" icon={<FaDiscord />} label="Discord" />
              <SocialLink href="https://twitch.tv" icon={<FaTwitch />} label="Twitch" />
              <SocialLink href="https://itch.io" icon={<SiItchdotio />} label="Itch.io" />
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6 text-accent">
              {t('navigation.quickLinks')}
            </h4>
            <div className="w-12 h-1 bg-secondary rounded-full mb-6"></div>
            <ul className="space-y-3">
              <FooterNavItem to="/">{t('navigation.home')}</FooterNavItem>
              <FooterNavItem to="/services">{t('navigation.services')}</FooterNavItem>
              <FooterNavItem to="/portfolio">{t('navigation.portfolio')}</FooterNavItem>
              <FooterNavItem to="/testimonials">{t('navigation.testimonials')}</FooterNavItem>
              <FooterNavItem to="/contact">{t('navigation.contact')}</FooterNavItem>
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6 text-accent">
              {t('navigation.services')}
            </h4>
            <div className="w-12 h-1 bg-secondary rounded-full mb-6"></div>
            <ul className="space-y-3">
              <FooterNavItem to="/services#game-translation">Game Translation</FooterNavItem>
              <FooterNavItem to="/services#website-localization">Website Localization</FooterNavItem>
              <FooterNavItem to="/services#steam-page">Steam Page Localization</FooterNavItem>
              <FooterNavItem to="/services#ongoing-support">Ongoing Support</FooterNavItem>
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4 className="text-sm uppercase tracking-wider font-semibold mb-6 text-accent">
              {t('contact.title')}
            </h4>
            <div className="w-12 h-1 bg-secondary rounded-full mb-6"></div>
            <ul className="space-y-5">
              <li className="text-white/80">
                <span className="block text-white mb-1 font-medium">{t('contact.email')}</span>
                <a href="mailto:contact@natuxtranslations.com" className="hover:text-accent transition-colors duration-300">
                  contact@natuxtranslations.com
                </a>
              </li>
              <li className="text-white/80">
                <span className="block text-white mb-1 font-medium">{t('contact.phone')}</span>
                <a href="tel:+33123456789" className="hover:text-accent transition-colors duration-300">
                  +33 (0)1 23 45 67 89
                </a>
              </li>
              <li className="text-white/80">
                <span className="block text-white mb-1 font-medium">{t('contact.location')}</span>
                Paris, France
              </li>
            </ul>
          </div>
        </div>
        
        {/* Gaming-inspired separator */}
        <div className="mt-16 mb-8 w-full h-px bg-white/20 relative">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-1 bg-accent"></div>
        </div>
        
        {/* Footer Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-white/60 mb-6 md:mb-0">
            &copy; {currentYear} NatuXtranslations. {t('footer.rights')}
          </p>
          <div className="flex space-x-8">
            <Link to="/privacy-policy" className="text-white/60 hover:text-accent transition-colors duration-300">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms-of-service" className="text-white/60 hover:text-accent transition-colors duration-300">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper components
const SocialLink = ({ href, icon, label }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    aria-label={label}
    className="text-white/80 hover:text-accent transition-all duration-300 text-lg hover:-translate-y-1"
  >
    {icon}
  </a>
);

const FooterNavItem = ({ to, children }) => (
  <li>
    <Link 
      to={to} 
      className="text-white/80 hover:text-accent transition-all duration-300 inline-block hover:translate-x-1"
    >
      {children}
    </Link>
  </li>
);

export default Footer;
